import { createFromTemplate, slot } from '../../core/dom/template.js';
import { createElement, delegate } from '../../core/dom/helpers.js';
import { normalizePath } from '../../core/routing/router.js';
import { safeStorageGet, safeStorageSet } from '../../core/storage/safe-storage.js';
import { filterMenuTree } from '../../core/menu/menu-tree-filter.js';
import tpl from './sidebar.tpl.js';

const EXPANDED_KEY = 'adminsr_sidebar_expanded';

/**
 * @param {HTMLElement} container
 * @param {{
 *   menuTree: import('../../contracts/menu.js').MenuNode[],
 *   activeRoute?: string,
 *   onNavigate: (path: string) => void
 * }} options
 */
export function createSidebar(container, { menuTree, activeRoute, onNavigate }) {
  const root = createFromTemplate(tpl);
  const menuSlot = slot(root, 'menu');
  const searchSlot = /** @type {HTMLInputElement} */ (slot(root, 'search'));
  container.appendChild(root);

  /** @type {Set<string>} */
  let expandedGroups = loadExpanded(menuTree);

  /** @type {string | undefined} */
  let currentActive = activeRoute;

  // Current nav-search query (trimmed). Empty string = unfiltered. ALL render
  // paths go through renderFiltered() which consults this, so the input text
  // and the rendered tree can never drift.
  let query = '';

  // Auto-expand group containing initial active route
  if (activeRoute) {
    expandAncestors(menuTree, activeRoute, expandedGroups);
    saveExpanded(expandedGroups);
  }

  /** @type {Array<() => void>} */
  const cleanups = [];

  // Delegate link clicks
  cleanups.push(delegate(root, 'click', 'a[data-path]', (event, target) => {
    // Preserve browser default for modified clicks
    const me = /** @type {MouseEvent} */ (event);
    if (me.metaKey || me.ctrlKey || me.shiftKey || me.button !== 0) return;

    event.preventDefault();
    const path = target.getAttribute('data-path');
    if (!path) return;
    // The user picked a result — clear any active filter so the sidebar
    // returns to its normal full view after the jump.
    clearQuery();
    onNavigate(path);
  }));

  // Delegate toggle clicks. While filtering, groups are force-expanded and
  // their toggles are inert (disabled), so ignore toggle clicks entirely.
  cleanups.push(delegate(root, 'click', 'button[data-toggle]', (_event, target) => {
    if (query) return;
    const groupId = target.getAttribute('data-toggle');
    if (!groupId) return;

    if (expandedGroups.has(groupId)) {
      expandedGroups.delete(groupId);
    } else {
      expandedGroups.add(groupId);
    }
    saveExpanded(expandedGroups);
    renderFiltered();
  }));

  // Search input — filter the menu live. Routed through renderFiltered().
  const onSearchInput = () => {
    query = searchSlot.value.trim();
    renderFiltered();
  };
  searchSlot.addEventListener('input', onSearchInput);
  cleanups.push(() => searchSlot.removeEventListener('input', onSearchInput));

  /** @param {KeyboardEvent} e */
  const onSearchKeydown = (e) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    if (searchSlot.value !== '') {
      clearQuery(); // first Escape clears the query
    } else {
      searchSlot.blur(); // already empty → release focus
    }
  };
  searchSlot.addEventListener('keydown', onSearchKeydown);
  cleanups.push(() => searchSlot.removeEventListener('keydown', onSearchKeydown));

  renderFiltered();

  /**
   * @param {string} url
   * @returns {HTMLElement}
   */
  function createIcon(url) {
    const img = createElement('img', {
      class: 'sidebar-icon',
      'aria-hidden': 'true',
    });
    img.setAttribute('src', url);
    img.setAttribute('alt', '');
    return img;
  }

  /**
   * The single render entry-point. Reads the current `query`:
   *  - empty → render the full tree honoring persisted `expandedGroups`.
   *  - non-empty → render the pruned tree with every surviving group
   *    force-expanded (and inert toggles); no-results row when nothing matches.
   *    Force-expansion is NOT persisted.
   */
  function renderFiltered() {
    if (query) {
      const pruned = filterMenuTree(menuTree, query);
      if (pruned.length === 0) {
        menuSlot.innerHTML = '';
        menuSlot.appendChild(createElement('li', { class: 'sidebar-empty' }, ['No matches']));
        return;
      }
      renderTree(pruned, currentActive, true);
    } else {
      renderTree(menuTree, currentActive, false);
    }
  }

  /** Clear the search query + input and re-render the full tree. */
  function clearQuery() {
    if (query === '' && searchSlot.value === '') return;
    query = '';
    searchSlot.value = '';
    renderFiltered();
  }

  /**
   * @param {import('../../contracts/menu.js').MenuNode[]} tree
   * @param {string} [active]
   * @param {boolean} [forceExpand] When true, all groups render expanded with
   *   inert toggles (filtering view); persisted expand state is ignored.
   */
  function renderTree(tree, active, forceExpand = false) {
    menuSlot.innerHTML = '';
    for (const node of tree) {
      menuSlot.appendChild(renderNode(node, active, forceExpand));
    }
  }

  /**
   * @param {import('../../contracts/menu.js').MenuNode} node
   * @param {string} [active]
   * @param {boolean} [forceExpand]
   * @returns {HTMLElement}
   */
  function renderNode(node, active, forceExpand = false) {
    const li = createElement('li', { class: 'sidebar-item' });

    if (node.children.length > 0) {
      // Parent node with toggle. While filtering (forceExpand) the group is
      // always expanded and its toggle is inert (disabled + aria-disabled).
      const isExpanded = forceExpand || expandedGroups.has(node.id);
      /** @type {Record<string, string>} */
      const btnAttrs = {
        class: `sidebar-toggle${forceExpand ? ' sidebar-toggle--disabled' : ''}`,
        'data-toggle': node.id,
        'aria-expanded': String(isExpanded),
      };
      if (forceExpand) {
        btnAttrs['aria-disabled'] = 'true';
      }
      const btn = createElement('button', btnAttrs);
      if (forceExpand) {
        /** @type {HTMLButtonElement} */ (btn).disabled = true;
      }
      if (node.icon_url) {
        btn.appendChild(createIcon(node.icon_url));
      }
      btn.appendChild(document.createTextNode(node.label));
      li.appendChild(btn);

      const childList = createElement('ul', {
        class: `sidebar-group${isExpanded ? '' : ' sidebar-group--collapsed'}`,
      });
      for (const child of node.children) {
        childList.appendChild(renderNode(child, active, forceExpand));
      }
      li.appendChild(childList);
    } else {
      // Leaf node with link
      const path = normalizePath(node.path);
      const isActive = active === path;
      /** @type {Record<string, string>} */
      const attrs = {
        href: `#${path}`,
        'data-path': path,
        class: `sidebar-link${isActive ? ' sidebar-link--active' : ''}`,
      };
      if (isActive) {
        attrs['aria-current'] = 'page';
      }
      const link = createElement('a', attrs);
      if (node.icon_url) {
        link.appendChild(createIcon(node.icon_url));
      }
      link.appendChild(document.createTextNode(node.label));
      li.appendChild(link);
    }

    return li;
  }

  /**
   * Load persisted expanded groups, intersecting with current group IDs.
   * @param {import('../../contracts/menu.js').MenuNode[]} tree
   * @returns {Set<string>}
   */
  function loadExpanded(tree) {
    // Migrate from old collapsed-based key
    try { localStorage.removeItem('adminsr_sidebar_collapsed'); } catch (_) { /* ignore */ }

    const persisted = safeStorageGet(EXPANDED_KEY, []);
    const groupIds = collectGroupIds(tree);
    const result = new Set();
    if (Array.isArray(persisted)) {
      for (const id of persisted) {
        if (groupIds.has(id)) result.add(id);
      }
    }
    return result;
  }

  /**
   * Collect all parent (group) node IDs from tree.
   * @param {import('../../contracts/menu.js').MenuNode[]} nodes
   * @returns {Set<string>}
   */
  function collectGroupIds(nodes) {
    const ids = new Set();
    for (const node of nodes) {
      if (node.children.length > 0) {
        ids.add(node.id);
        for (const id of collectGroupIds(node.children)) {
          ids.add(id);
        }
      }
    }
    return ids;
  }

  /**
   * @param {Set<string>} expanded
   */
  function saveExpanded(expanded) {
    safeStorageSet(EXPANDED_KEY, [...expanded]);
  }

  /**
   * Find the group ancestor(s) of a leaf node matching the active path,
   * and add them to the expanded set.
   * @param {import('../../contracts/menu.js').MenuNode[]} nodes
   * @param {string} activePath
   * @param {Set<string>} expanded
   * @returns {boolean} true if active path found in subtree
   */
  function expandAncestors(nodes, activePath, expanded) {
    for (const node of nodes) {
      if (node.children.length > 0) {
        if (expandAncestors(node.children, activePath, expanded)) {
          expanded.add(node.id);
          return true;
        }
      } else {
        if (normalizePath(node.path) === activePath) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Update menu tree and active route.
   * Clears any active search query — the old query may reference items that no
   * longer exist in the new tree; the fresh full menu is shown.
   * @param {import('../../contracts/menu.js').MenuNode[]} newTree
   * @param {string} [active]
   */
  function update(newTree, active) {
    menuTree = newTree;
    currentActive = active;
    query = '';
    searchSlot.value = '';
    expandedGroups = new Set();
    if (active) {
      expandAncestors(newTree, active, expandedGroups);
    }
    saveExpanded(expandedGroups);
    renderFiltered();
  }

  /**
   * Update only the active route highlight.
   * Collapses all groups, then expands only the ancestor(s) of the active page.
   * Clears any active search query so the active item shows in the full menu.
   * @param {string} route
   */
  function setActiveRoute(route) {
    currentActive = route;
    query = '';
    searchSlot.value = '';
    expandedGroups = new Set();
    expandAncestors(menuTree, route, expandedGroups);
    saveExpanded(expandedGroups);
    renderFiltered();
  }

  function destroy() {
    for (const cleanup of cleanups) cleanup();
    cleanups.length = 0;
    root.remove();
  }

  return { update, setActiveRoute, destroy };
}
